import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { DataGrid, Toolbar } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { isEmpty } from "utils/validationUtil";
import { useResizableColumnWidths } from "app/hooks/useResizableColumnWidths";
import { Box, Button } from "@mui/material";
import { Add as AddIcon } from "components/icons";
import { GridQuickSearchField } from "./GridQuickSearchField";

import { DEFAULT_TABLE_PAGE_SIZE } from "AppConstants";

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Reusable server-driven DataGrid for the Master Data section.
 *
 * Every master grid runs in server pagination/sort/filter mode against a
 * backend that returns a PageResponse envelope. Each screen supplies its own
 * columns, sort-field map and fetch handlers (via useServerPaginatedGrid); this
 * component owns the MUI X wiring so the UX stays identical across screens.
 */
const GridToolbar = ({ onAdd, addLabel, onSecondaryAdd, secondaryAddLabel, showSearch }) => (
    <Toolbar>
        <Box sx={{ py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, width: "100%" }}>
            {onAdd || onSecondaryAdd ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    {onAdd && (
                        <Button
                            startIcon={<AddIcon />}
                            onClick={onAdd}
                            sx={{ minWidth: 140 }}
                            variant="contained"
                            color="success"
                        >
                            {addLabel || "Add"}
                        </Button>
                    )}
                    {onSecondaryAdd && (
                        <Button
                            startIcon={<AddIcon />}
                            onClick={onSecondaryAdd}
                            sx={{ minWidth: 140 }}
                            variant="contained"
                            color="success"
                        >
                            {secondaryAddLabel || "Add"}
                        </Button>
                    )}
                </Box>
            ) : (
                <span />
            )}
            {showSearch && <GridQuickSearchField />}
        </Box>
    </Toolbar>
);

GridToolbar.propTypes = {
    onAdd: PropTypes.func,
    addLabel: PropTypes.string,
    onSecondaryAdd: PropTypes.func,
    secondaryAddLabel: PropTypes.string,
    showSearch: PropTypes.bool,
};

export const PaginatedDataGrid = (props) => {
    const {
        rows,
        columns,
        totalElements,
        page,
        pageSize,
        sortBy,
        sortDirection,
        searchQuery,
        isLoading,
        sortFieldMap,
        defaultSortBy,
        defaultSortDirection = "ASC",
        onPaginationModelChange,
        onSortModelChange,
        onSearchChange,
        onAdd,
        addLabel,
        onSecondaryAdd,
        secondaryAddLabel,
        getRowId,
        pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
        columnVisibilityModel,
        height = 500,
    } = props;

    // Keeps a dragged column width alive across re-renders — every master view rebuilds its
    // `columns` array inline on each render, which would otherwise reset it.
    const { columns: sizedColumns, onColumnWidthChange } = useResizableColumnWidths(columns);

    const getData = () => (isEmpty(rows) ? [] : rows);

    const handleSortModelChange = (model) => {
        if (!onSortModelChange) return;
        const fallbackField = defaultSortBy || Object.values(sortFieldMap || {})[0];
        if (!model || model.length === 0) {
            onSortModelChange({ sortBy: fallbackField, sortDirection: defaultSortDirection });
            return;
        }
        const { field, sort } = model[0];
        const mappedField = (sortFieldMap && sortFieldMap[field]) || fallbackField;
        onSortModelChange({
            sortBy: mappedField,
            sortDirection: (sort || "asc").toUpperCase(),
        });
    };

    // Stable references for the controlled DataGrid props. MUI X v8 treats prop identity
    // changes on `sortModel`/`paginationModel` as real state changes, which fires a
    // spurious `sortModelChange` -> `navigateToStart` -> `setPage(0)` chain after every
    // legitimate page change.
    const sortModel = useMemo(
        () => (sortBy && sortFieldMap && sortFieldMap[sortBy]
            ? [{ field: sortBy, sort: (sortDirection || defaultSortDirection).toLowerCase() }]
            : []),
        [sortBy, sortDirection, sortFieldMap, defaultSortDirection]
    );

    const paginationModel = useMemo(
        () => ({ page: page ?? 0, pageSize: pageSize ?? DEFAULT_TABLE_PAGE_SIZE }),
        [page, pageSize]
    );

    const filterModel = useMemo(
        () => ({ items: [], quickFilterValues: searchQuery ? [searchQuery] : [] }),
        [searchQuery]
    );

    const handleFilterModelChange = (model) => {
        if (!onSearchChange) return;
        const nextQuery = (model?.quickFilterValues ?? []).join(" ").trim();
        onSearchChange(nextQuery);
    };

    const showSearch = Boolean(onSearchChange);

    const toolbarSlotProps = useMemo(
        () => ({ onAdd, addLabel, onSecondaryAdd, secondaryAddLabel, showSearch }),
        [onAdd, addLabel, onSecondaryAdd, secondaryAddLabel, showSearch]
    );

    return (
        <MDBox py={1.5} px={2} height={height}>
            <DataGrid
                density="compact"
                rows={getData()}
                columns={sizedColumns}
                loading={isLoading}
                getRowId={getRowId}

                onColumnWidthChange={onColumnWidthChange}

                paginationMode="server"
                /* While the first fetch is in flight totalElements is still 0. Passing rowCount=0
                 * makes MUI think any page > 0 is "out of bounds" and fires a phantom
                 * onPaginationModelChange({page:0}) that clobbers legitimate user clicks. Sentinel
                 * -1 tells MUI "total unknown - don't validate page bounds". */
                rowCount={totalElements > 0 ? totalElements : -1}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                pageSizeOptions={pageSizeOptions}

                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}

                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}

                showToolbar
                slots={{ toolbar: GridToolbar }}
                slotProps={{ toolbar: toolbarSlotProps }}

                initialState={columnVisibilityModel ? { columns: { columnVisibilityModel } } : undefined}
                disableRowSelectionOnClick
            />
        </MDBox>
    );
};

PaginatedDataGrid.propTypes = {
    rows: PropTypes.array,
    columns: PropTypes.array.isRequired,
    totalElements: PropTypes.number,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    sortBy: PropTypes.string,
    sortDirection: PropTypes.string,
    searchQuery: PropTypes.string,
    isLoading: PropTypes.bool,
    sortFieldMap: PropTypes.object,
    defaultSortBy: PropTypes.string,
    defaultSortDirection: PropTypes.string,
    onPaginationModelChange: PropTypes.func.isRequired,
    onSortModelChange: PropTypes.func.isRequired,
    onSearchChange: PropTypes.func,
    onAdd: PropTypes.func,
    addLabel: PropTypes.string,
    onSecondaryAdd: PropTypes.func,
    secondaryAddLabel: PropTypes.string,
    getRowId: PropTypes.func,
    pageSizeOptions: PropTypes.array,
    columnVisibilityModel: PropTypes.object,
    height: PropTypes.number,
};

export default PaginatedDataGrid;
